import { Injectable } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { DynamicQueryContainer } from './dynamic-query-container';
import { DynamicQueryMessageAdapter } from './dynamic-query-message-adapter';
import { StreamService } from 'common/dynamicquery/stream.service';
import { Observable, Subject } from 'rxjs';
import { isDynamicQueryMethod } from 'common/dynamicquery/dynamic-query-method';

@Injectable({
  providedIn: 'root'
})
export class DynamicQueryManagerService {
  private _isConnected: boolean = false;
  private _queue = [];
  private _queryContainer;
  private readonly _connect$ = new Subject<boolean>();
  private readonly _messageHandlers;

  constructor(private _restApi: BcRestApi, private _rxEmitter: RxEmitter, private _stream: StreamService) {
    this._queryContainer = DynamicQueryContainer(
      this._queryCreated.bind(this),
      this._queryClosed.bind(this),
      this._queryReset.bind(this)
    );

    this._messageHandlers = {
      reset: this._resetStream.bind(this),
      defaultHandler: (method: string, params, _params2?) => {
        if (!isDynamicQueryMethod(method)) {
          return;
        }

        const dq = this._queryContainer.get(params && params.DynamicQueryID);
        if (dq) {
          const adapter = new DynamicQueryMessageAdapter(dq);
          adapter.forwardMessage(method, params);
        } else if (!this._queryContainer.isQueryDeleted(params.DynamicQueryID)) {
          throw new Error('Incoming message for a non existing dynamic query. ' + params.DynamicQueryID);
        }
      },
      mediaMessage: (method, params) => {
        this._rxEmitter.send(MessageBusEvents.MediaMessage, params);
      }
    };

    this._rxEmitter.subscribe(MessageBusEvents.WebSocketConnected, () => {
      this._isConnected = true;
      this._executeQueue();
      this._connect$.next(true);
    });
  }

  query(queryName, values) {
    if (!this._isConnected) {
      return new Promise((resolve) => {
        this._queue.push(() => {
          const dq = this._createQuery(queryName, values);
          resolve(dq);
        });
      });
    }

    return Promise.resolve(this._createQuery(queryName, values));
  }

  public isConnected() {
    return this._isConnected;
  }

  public connect(): Observable<boolean> {
    this._stream.connect(undefined, this._onMessage.bind(this)).catch(() => this._connect$.next(false));

    return this._connect$.asObservable();
  }

  public close(): Promise<void> {
    return new Promise((resolve) => {
      let closingPromises = this._queryContainer.closeAll();
      Promise.all(closingPromises)
        .then(() => {
          this._handleClosingPromises(resolve);
        })
        .catch(() => {
          this._handleClosingPromises(resolve);
        });
    });
  }

  private _handleClosingPromises(resolve) {
    this._stream.close();
    this._isConnected = false;
    resolve();
  }

  private _onMessage(method, params, id) {
    // message
    if (this._messageHandlers[method]) {
      this._messageHandlers[method](method, params, id);
    } else {
      this._messageHandlers.defaultHandler(method, params, id);
    }
  }

  private _resetStream() {
    this._isConnected = false;
    this._stream.reset().then(() => {
      this._isConnected = true;
      this._queryContainer.resetAll();
      this._executeQueue();
    });
  }

  private _executeQueue() {
    for (let i = 0, length = this._queue.length; i < length; i++) {
      this._queue[i]();
    }
    this._queue = [];
  }

  private _createQuery(queryName, values) {
    return this._queryContainer.create(queryName, values);
  }

  private _queryCreated(dq) {
    this._restApi.createDynamicQuery(dq.query, dq.id, dq.values);
  }

  private _queryClosed(dq) {
    return this._restApi.deleteDynamicQuery(dq.id);
  }

  private _queryReset(dq) {
    if (!dq.isReloading()) {
      if (!this._isConnected) {
        return this._queryResetAfterConnected(dq);
      }
      this._queryResetWhenConnected(dq);
    }
    return Promise.resolve(dq);
  }

  private _queryResetWhenConnected(dq) {
    this._queryClosed(dq);
    this._queryCreated(dq);
  }

  private _queryResetAfterConnected(dq) {
    return new Promise((resolve) => {
      const listener = () => {
        if (this._isConnected) {
          this._queryResetWhenConnected(dq);
          this._stream.removeListener(listener);
          resolve(dq);
        }
      };

      this._stream.addListener(listener);
    });
  }
}
